// src/pages/ObjectRemover.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useProductStore } from "../store/useProductStore";
import GenerateObjectRemoverModal from "../components/GenerateObjectRemoverModal";
import placeholderImage from "../assets/placeholder.jpg";
import {
  Plus,
  Download,
  Trash2,
  X,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";
import Loader from "../components/Loader"; // ✅ NEW

const FEATURE_KEY = "object-remover";
const PAGE_SIZE = 12;

export default function ObjectRemover() {
  const { getToken } = useAuth();
  const { selectedProduct } = useProductStore();
  const { projectId: paramProjectId } = useParams();
  const navigate = useNavigate();

  // items: [{ id, url, status, cloudinary_public_id? }]
  const [items, setItems] = useState([]);
  const [productImages, setProductImages] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [preview, setPreview] = useState(null); // { id, url }
  const [error, setError] = useState(null);

  // per-user image quota (for greying out CTA)
  const [quota, setQuota] = useState({
    loading: true,
    unlimited: false,
    remaining: 0,
    used: 0,
    baseCap: 0,
    imagesExtra: 0,
    period_start: null,
    period_end: null,

    // ✅ subscription/payment/account gating flags from backend
    is_blocked: false,
    payment_blocked: false,
    sub_status: null,
  });

  // infinite scroll state
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);

  const previewBackdrop = useRef(null);
  const pollRef = useRef(null);

  // IMPORTANT: URL param is source of truth (prevents ping-pong when navigating via notifications)
  const projectId = paramProjectId || selectedProduct?.id;
  const hasProject = !!projectId;

  /* --------------------------- Helpers --------------------------- */
  const toArray = useCallback((val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      const t = val.trim();
      if (!t || t === "{}") return [];
      if (t.startsWith("{") && t.endsWith("}")) {
        return t
          .slice(1, -1)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      try {
        const parsed = JSON.parse(t);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }, []);

  // Force a direct download from Cloudinary (no preview/new tab)
  const toCloudinaryDownloadUrl = useCallback((url) => {
    if (!url) return url;
    try {
      const u = new URL(url);
      if (u.hostname.includes("cloudinary") && u.pathname.includes("/upload/")) {
        u.pathname = u.pathname.replace("/upload/", "/upload/fl_attachment/");
        return u.toString();
      }
    } catch {
      // ignore parse errors
    }
    return url;
  }, []);

  // prefer assetsByFeature when available; fallback to flat assets
  const pickObjectRemoverAssets = useCallback(
    (data) => data?.assetsByFeature?.[FEATURE_KEY] || data?.assets || [],
    []
  );

  // safely parse metadata, even if JSON string
  const parseMeta = useCallback((m) => {
    if (!m) return {};
    if (typeof m === "string") {
      try {
        return JSON.parse(m);
      } catch {
        return {};
      }
    }
    return m;
  }, []);

  // tolerant mapping + feature filter + stable sort
  const mapAssets = useCallback(
    (rows) => {
      const PENDING_STATES = new Set(["pending", "queued", "processing"]);

      return (rows || [])
        .map((a) => {
          const meta = parseMeta(a?.metadata);
          const statusRaw = String(meta?.status || "finished").toLowerCase();
          const feature = String(meta?.feature || "").toLowerCase();

          // ✅ Hide soft-deleted + failed rows (must stay in DB for quota, but disappear from UI)
          const isDeleted = meta?.deleted === true || statusRaw === "deleted";
          const isFailed = statusRaw === "failed";
          if (isDeleted || isFailed) return null;

          const rawUrl = a?.url || "";

          // tolerate placeholder URLs (they usually include #<placeholderId>)
          const isPending =
            PENDING_STATES.has(statusRaw) ||
            !rawUrl ||
            rawUrl === "/spinner.gif" ||
            String(rawUrl).includes("#");

          return {
            id: a?.id,
            url: isPending ? "/spinner.gif" : rawUrl,
            status: isPending ? statusRaw || "pending" : "finished",
            cloudinary_public_id: meta?.cloudinary_public_id,
            feature,
            created_at: a?.created_at || a?.createdAt || 0,
          };
        })
        .filter(Boolean)
        .filter((a) => a.feature === FEATURE_KEY)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    },
    [parseMeta]
  );

  const anyPending = useMemo(() => {
    const PENDING_STATES = new Set(["pending", "queued", "processing"]);
    return items.some(
      (i) => PENDING_STATES.has(i.status) || !i.url || i.url === "/spinner.gif"
    );
  }, [items]);

  const visibleItems = useMemo(
    () => items.slice(0, page * PAGE_SIZE),
    [items, page]
  );

  // ✅ make this stable (DO NOT depend on `page`)
  const recalcHasMore = useCallback((arr, pageNum) => {
    const p = Number(pageNum) || 1;
    setHasMore(p * PAGE_SIZE < (arr?.length || 0));
  }, []);

  /* --------------------------- Quota fetcher --------------------------- */
  const fetchQuota = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/object-remover/quota`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuota({ loading: false, ...res.data });
    } catch (e) {
      // non-fatal
      setQuota((q) => ({ ...q, loading: false }));
    }
  }, [getToken]);

  /* --------------------------- Fetch project data --------------------------- */
  const fetchProjectData = useCallback(async () => {
    // ✅ If no analyzed project yet, don’t error — keep CTA visible and empty grid.
    if (!projectId) {
      setProductImages([]);
      setItems((prev) => prev.filter((p) => String(p.id).startsWith("temp-")));
      setError(null);
      setInitialLoading(false);
      return;
    }

    setInitialLoading(true);
    setPage(1);
    pageRef.current = 1;

    try {
      const token = await getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // analyzed images for picker (modal)
      setProductImages(toArray(res.data.images));

      // assets filtered by feature (hide deleted/failed, keep pending spinners)
      const serverList = mapAssets(pickObjectRemoverAssets(res.data));

      // MERGE: preserve local temp spinners on top, dedup if server has the same id
      setItems((prev) => {
        const temps = prev.filter((p) => String(p.id).startsWith("temp-"));
        const serverById = new Map(serverList.map((a) => [String(a.id), a]));
        const keptTemps = temps.filter((t) => !serverById.has(String(t.id)));
        const merged = [...keptTemps, ...serverList];
        recalcHasMore(merged, 1);
        return merged;
      });

      setError(null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Fetch project failed", e?.response?.data || e.message);
      setError(
        e?.response?.status === 401
          ? "Session expired. Please sign in again."
          : "Failed to fetch project data"
      );
    } finally {
      setInitialLoading(false);
    }
  }, [
    getToken,
    projectId,
    mapAssets,
    pickObjectRemoverAssets,
    toArray,
    recalcHasMore,
  ]);

  // Keep pageRef in sync (so merges can compute hasMore without causing refetch loops)
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // Also keep hasMore in sync whenever page/items change
  useEffect(() => {
    setHasMore(page * PAGE_SIZE < items.length);
  }, [page, items.length]);

  // On project switch: clear stale UI + poll, then refetch
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setItems([]);
    setPage(1);
    pageRef.current = 1;
    setHasMore(true);
    setError(null);
    void fetchProjectData();
    void fetchQuota();
  }, [projectId, fetchProjectData, fetchQuota]);

  // Only navigate from dropdown when URL does not already specify a projectId (prevents ping-pong)
  useEffect(() => {
    if (selectedProduct?.id && !paramProjectId) {
      navigate(`/generate/object-remover/${selectedProduct.id}`);
    }
  }, [selectedProduct, paramProjectId, navigate]);

  // Clear any active poll on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  /* ----------------------- Polling (auto-refresh) ----------------------- */
  const beginPolling = useCallback(() => {
    if (!projectId) return;

    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const token = await getToken();
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const serverList = mapAssets(pickObjectRemoverAssets(res.data));

        // MERGE: server truth + keep any local temps not yet in DB
        setItems((prev) => {
          const temps = prev.filter((p) => String(p.id).startsWith("temp-"));
          const serverById = new Map(serverList.map((a) => [String(a.id), a]));
          const keptTemps = temps.filter((t) => !serverById.has(String(t.id)));
          const merged = [...keptTemps, ...serverList];
          recalcHasMore(merged, pageRef.current);
          return merged;
        });

        const stillPending = serverList.some((a) => a.url === "/spinner.gif");

        if (!stillPending || attempts >= 40) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          void fetchQuota();
        }
      } catch (err) {
        if (err?.response?.status === 401) {
          setError("Session expired. Please sign in again.");
          clearInterval(pollRef.current);
          pollRef.current = null;
          return;
        }
        if (attempts >= 40) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          void fetchQuota();
        }
      }
    }, 5000);
  }, [
    getToken,
    projectId,
    mapAssets,
    pickObjectRemoverAssets,
    fetchQuota,
    recalcHasMore,
  ]);

  // If user navigates in with pending items (or a job starts elsewhere), auto-start polling
  useEffect(() => {
    if (anyPending) beginPolling();
  }, [anyPending, beginPolling]);

  /* ------------------------ Fire a generate request ------------------------ */
  // The modal should pass us: imageUrl (string), maskDataUrl (data:image/png;base64,...)
  const handleGenerate = useCallback(
    async (imageUrl, maskDataUrl) => {
      setError(null);

      // ✅ No analyzed product → go to Analyze (same CTA logic as RemoveBackground)
      if (!projectId) {
        setError(
          "Please analyze a product first so we can save your generated assets."
        );
        navigate("/analyze");
        return;
      }

      // account/payment gating (server enforces too)
      if (quota.is_blocked) {
        setError("Your account is blocked. Please contact support.");
        return;
      }
      if (quota.payment_blocked) {
        setError(
          "Your subscription payment needs attention. Please update billing to continue."
        );
        navigate("/pricing");
        return;
      }

      // quota gate on client (server enforces too)
      if (!quota.unlimited && (quota.remaining || 0) <= 0) {
        navigate("/pricing");
        return;
      }

      const tempId = `temp-${Date.now()}`;

      // OPTIMISTIC: show spinner immediately & start polling right away
      setItems((prev) => {
        const next = [
          { id: tempId, url: "/spinner.gif", status: "pending" },
          ...prev,
        ];
        recalcHasMore(next, pageRef.current);
        return next;
      });
      beginPolling();

      try {
        const token = await getToken();

        // upload mask dataURL -> Cloudinary URL
        const maskUpload = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/object-remover/mask`,
          { dataUrl: maskDataUrl },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const maskUrl = maskUpload.data?.url;
        if (!maskUrl) throw new Error("Mask upload failed");

        // queue Phot.AI job (server inserts a pending asset row) — returns assetId
        const queueRes = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/object-remover/remove`,
          { imageUrl, maskUrl, projectId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const assetId = queueRes.data?.assetId;

        // replace temp card id with real asset id & mark as queued
        if (assetId) {
          setItems((prev) =>
            prev.map((it) =>
              it.id === tempId
                ? {
                    ...it,
                    id: assetId,
                    status: "queued",
                    url: it.url || "/spinner.gif",
                  }
                : it
            )
          );
        }

        // optimistic quota decrement (pending counts as used)
        if (!quota.unlimited) {
          setQuota((q) => ({
            ...q,
            remaining: Math.max(0, (q.remaining || 0) - 1),
            used: (q.used || 0) + 1,
          }));
        }
      } catch (e) {
        setItems((prev) => {
          const next = prev.filter((x) => x.id !== tempId);
          recalcHasMore(next, pageRef.current);
          return next;
        });

        const code = e?.response?.data?.code || "";
        if (code === "images_quota_exceeded" || code === "payment_required") {
          navigate("/pricing");
        }

        if (code === "images_quota_exceeded") {
          setError(
            "You’ve reached your image limit for this cycle. Upgrade or add images."
          );
        } else if (code === "payment_required") {
          setError(
            "Your payment needs attention. Please update billing to continue."
          );
        } else if (code === "account_blocked") {
          setError("Your account is blocked. Please contact support.");
        } else {
          setError(e?.response?.data?.error || "Failed to remove object. Try again.");
        }
      }
    },
    [getToken, projectId, beginPolling, quota, recalcHasMore, navigate]
  );

  /* ------------------------------ Download --------------------------------- */
  const handleDownload = useCallback(
    (url) => {
      const dl = toCloudinaryDownloadUrl(url);
      const a = document.createElement("a");
      a.href = dl;
      a.download = ""; // Cloudinary sets filename via fl_attachment
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    [toCloudinaryDownloadUrl]
  );

  /* -------------------------------- Delete --------------------------------- */
  const handleDelete = useCallback(
    async (assetId) => {
      // If it's a temp card, just remove it locally
      if (String(assetId).startsWith("temp-")) {
        setItems((prev) => {
          const next = prev.filter((img) => img.id !== assetId);
          recalcHasMore(next, pageRef.current);
          return next;
        });
        return;
      }

      if (!window.confirm("Delete this image?")) return;

      try {
        const token = await getToken();

        // ✅ IMPORTANT:
        // Use feature soft-delete endpoint so the assets row stays in DB (quota does NOT decrease)
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URL}/api/object-remover/delete`,
          {
            headers: { Authorization: `Bearer ${token}` },
            data: { projectId, assetId },
          }
        );

        setItems((prev) => {
          const next = prev.filter((img) => img.id !== assetId);
          if (preview?.id === assetId) setPreview(null);
          recalcHasMore(next, pageRef.current);
          return next;
        });

        // Quota should remain unchanged now, but keep UI in sync
        void fetchQuota();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Delete failed", e?.response?.data || e?.message);
        // eslint-disable-next-line no-alert
        alert("Delete failed");
      }
    },
    [getToken, preview, fetchQuota, recalcHasMore, projectId]
  );

  /* --------------------------- Infinite Scroll --------------------------- */
  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPage((p) => p + 1);
    setLoadingMore(false);
  }, [loadingMore]);

  useEffect(() => {
    if (!hasMore || initialLoading || loadingMore) return;
    const el = loaderRef.current;
    if (!el) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "100px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, initialLoading, loadingMore, loadMore]);

  /* --------------------------- Preview events --------------------------- */
  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => e.key === "Escape" && setPreview(null);
    const onClick = (e) => {
      if (previewBackdrop.current && e.target === previewBackdrop.current) {
        setPreview(null);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [preview]);

  // ✅ quota-based generation ability (not including "hasProject")
  const canGenerateQuota =
    !quota.is_blocked &&
    !quota.payment_blocked &&
    (quota.unlimited || (quota.remaining || 0) > 0);

  const totalImages = quota.unlimited
    ? Infinity
    : (quota.baseCap || 0) + (quota.imagesExtra || 0);
  const remaining = quota.unlimited ? Infinity : quota.remaining || 0;
  const used = quota.unlimited ? 0 : quota.used || 0;

  const showBar =
    !quota.loading &&
    !quota.unlimited &&
    Number.isFinite(totalImages) &&
    totalImages > 0;
  const pct = showBar
    ? Math.min(100, Math.max(0, Math.round((used / totalImages) * 100)))
    : 0;

  // ✅ centered loader condition
  const isInitialLoading = initialLoading && !error && items.length === 0;

  /* -------------------------------- Render -------------------------------- */
  return (
    <div className="w-full px-2 sm:px-4 md:px-8 py-5 sm:py-8">
      <div className="max-w-[1600px] mx-auto bg-[#15152B] border border-[#2A2A40] rounded-3xl px-2 sm:px-4 md:px-8 py-6 sm:py-10 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            Object Remover
          </h1>

          {/* Quota pill */}
          {!quota.loading && (
            <div
              className={`flex items-center gap-3 rounded-2xl px-4 py-2 border ${
                quota.unlimited
                  ? "border-pink-600/60 bg-pink-600/10"
                  : quota.is_blocked
                  ? "border-red-500/40 bg-red-500/10"
                  : quota.payment_blocked
                  ? "border-amber-500/40 bg-amber-500/10"
                  : "border-[#2A2A40] bg-[#1C1C34]"
              }`}
              title={
                quota.unlimited
                  ? "Unlimited images this cycle"
                  : quota.is_blocked
                  ? "Account blocked"
                  : quota.payment_blocked
                  ? "Payment required"
                  : "Images usage this cycle"
              }
            >
              {quota.unlimited ? (
                <span className="text-pink-400 font-semibold text-sm sm:text-base">
                  Unlimited images
                </span>
              ) : quota.is_blocked ? (
                <span className="text-red-300 font-semibold text-sm sm:text-base">
                  Account blocked
                </span>
              ) : quota.payment_blocked ? (
                <span className="text-amber-300 font-semibold text-sm sm:text-base">
                  Payment required
                </span>
              ) : (
                <span className="text-white/80 text-sm sm:text-base">
                  <span className="font-semibold text-white">{remaining}</span>{" "}
                  left of{" "}
                  <span className="font-semibold text-white">
                    {Number.isFinite(totalImages) ? totalImages : "∞"}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Usage bar */}
        {showBar && (
          <div className="mb-3">
            <div className="h-2 w-full bg-[#21213a] rounded-full overflow-hidden border border-[#2A2A40]">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  background:
                    "linear-gradient(90deg, rgba(252,39,131,0.9) 0%, rgba(252,39,131,0.7) 100%)",
                }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-white/60">
              <span>Used: {used}</span>
              <span>Total: {Number.isFinite(totalImages) ? totalImages : "∞"}</span>
            </div>
          </div>
        )}

        <p className="text-white/70 mb-4 sm:mb-6 text-sm sm:text-base">
          Paint a mask over unwanted objects—we’ll remove them and save the result
          to your project.
        </p>

        {error && (
          <div className="mb-4 sm:mb-6 flex items-center gap-2 text-red-400">
            <AlertTriangle size={18} />
            <span>{error}</span>
          </div>
        )}

        {/* ✅ Loader vs skeleton vs grid */}
        {isInitialLoading ? (
          <div className="flex items-center justify-center py-10 sm:py-16">
            <Loader />
          </div>
        ) : initialLoading ? (
          <div className="grid gap-4 sm:gap-6 grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
            {Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <div
                key={i}
                className="rounded-2xl border border-[#2A2A40] bg-[#1C1C34] h-[210px] sm:h-[300px] animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div
            className="
              grid gap-4 sm:gap-6
              grid-cols-1
              xs:grid-cols-2
              md:grid-cols-3
              lg:grid-cols-4
              [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]
            "
          >
            {/* ✅ Create Card (always visible, same behavior as RemoveBackground) */}
            <div
              onClick={() => {
                // 1) No analyzed product -> go Analyze
                if (!hasProject) {
                  navigate("/analyze");
                  return;
                }

                // 2) Out of quota / payment issues -> go Pricing
                if (!canGenerateQuota) {
                  navigate("/pricing");
                  return;
                }

                // 3) Account blocked -> show error (keep here, but after hasProject)
                if (quota.is_blocked) {
                  setError("Your account is blocked. Please contact support.");
                  return;
                }

                setModalOpen(true);
              }}
              className={`flex flex-col items-center justify-center border-2 border-dashed border-[#3A3A50] text-white rounded-lg min-h-[160px] h-[210px] sm:h-[300px] transition text-center p-4 sm:p-6 ${
                !hasProject || !canGenerateQuota
                  ? "opacity-60 cursor-not-allowed"
                  : "cursor-pointer hover:border-pink-500 hover:text-pink-500"
              }`}
              title={
                !hasProject
                  ? "Analyze a product first to start generating"
                  : !canGenerateQuota
                  ? "Out of images. Click to manage plan."
                  : "Pick an image, paint the mask"
              }
            >
              <Plus className="w-7 h-7 sm:w-8 sm:h-8 mb-2" />
              <p className="font-semibold text-base">Remove Object</p>
              <p className="text-xs sm:text-sm text-gray-400 mt-1">
                {!hasProject
                  ? "Analyze your first product to get started"
                  : quota.unlimited
                  ? "Unlimited images"
                  : quota.payment_blocked
                  ? "Payment required to continue"
                  : `You have ${remaining} left of ${
                      Number.isFinite(totalImages) ? totalImages : "∞"
                    } this cycle`}
              </p>

              {!!hasProject && !canGenerateQuota && !quota.unlimited && (
                <p className="text-xs text-red-300 mt-2">
                  {quota.payment_blocked
                    ? "Update billing to continue."
                    : "You’ve reached your image limit. Upgrade or add images."}
                </p>
              )}
            </div>

            {/* Result / Pending cards */}
            {visibleItems.map(({ id, url, status }) => {
              const PENDING_STATES = new Set(["pending", "queued", "processing"]);
              const isPending =
                PENDING_STATES.has(status) || !url || url === "/spinner.gif";
              const displayUrl = isPending ? "/spinner.gif" : url;

              return (
                <div
                  key={id}
                  className="relative group bg-gradient-to-br from-[#1C1C2E] to-[#252538] border border-[#2A2A40] text-white rounded-2xl p-2 sm:p-3 transition transform hover:scale-[1.03] hover:shadow-xl hover:ring-1 hover:ring-pink-500/30 min-h-[160px] h-[210px] sm:h-[300px] flex items-center justify-center"
                  style={{ overflow: "hidden" }}
                >
                  {isPending ? (
                    <>
                      <img
                        src="/spinner.gif"
                        alt="Generating..."
                        className="object-contain w-full mx-auto rounded-lg opacity-70 animate-pulse"
                        draggable={false}
                        style={{
                          background: "#111124",
                          height: "100%",
                          maxHeight: "calc(100% - 32px)",
                        }}
                      />
                      <div className="absolute top-2 right-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(id);
                          }}
                          className="bg-black/80 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <img
                        src={displayUrl}
                        alt="Object Removed"
                        className="object-contain max-h-[88%] w-full mx-auto rounded-lg transition-opacity duration-300 group-hover:opacity-70 cursor-pointer"
                        onClick={() => setPreview({ id, url: displayUrl })}
                        onError={(e) => {
                          // eslint-disable-next-line no-param-reassign
                          e.currentTarget.src = placeholderImage;
                        }}
                        draggable={false}
                        style={{
                          background: "#111124",
                          height: "100%",
                          maxHeight: "calc(100% - 32px)",
                        }}
                      />

                      <div
                        className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none z-20"
                        style={{ background: "rgba(21, 21, 43, 0.15)" }}
                      >
                        <div className="flex gap-3 pointer-events-auto">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(displayUrl);
                            }}
                            className="bg-black/80 hover:bg-pink-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Download"
                          >
                            <Download size={20} />
                          </button>
                          <a
                            href={displayUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="bg-black/80 hover:bg-blue-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Open in new tab"
                          >
                            <ExternalLink size={20} />
                          </a>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(id);
                            }}
                            className="bg-black/80 hover:bg-red-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Delete"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Infinite loader */}
        {hasMore && !initialLoading && (
          <div
            ref={loaderRef}
            className="w-full flex justify-center py-8 pointer-events-none"
          >
            {loadingMore && (
              <img src="/spinner.gif" alt="Loading..." className="w-7 h-7 mx-auto" />
            )}
          </div>
        )}

        {/* Modal */}
        {modalOpen && (
          <GenerateObjectRemoverModal
            productImages={productImages}
            onClose={() => setModalOpen(false)}
            onGenerate={handleGenerate}
            projectId={projectId}
          />
        )}

        {/* Preview Modal */}
        {preview && (
          <div
            ref={previewBackdrop}
            className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center px-2 select-none"
            style={{ animation: "fadeIn 0.18s" }}
          >
            <div className="relative bg-[#22223a] rounded-2xl max-w-[98vw] shadow-2xl p-0 flex flex-col">
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="absolute top-2 right-2 bg-white/90 hover:bg-pink-600 text-black hover:text-white rounded-full p-2 shadow transition"
                title="Close"
              >
                <X size={21} />
              </button>

              <div className="p-6 flex flex-col items-center justify-center">
                <img
                  src={preview.url}
                  alt="Preview"
                  className="max-w-[95vw] max-h-[78vh] object-contain rounded-xl bg-black"
                  style={{ boxShadow: "0 4px 32px 0 rgba(30,25,60,0.5)" }}
                  draggable={false}
                />
              </div>

              <div className="flex justify-center gap-4 pb-5">
                <button
                  type="button"
                  onClick={() => handleDownload(preview.url)}
                  className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Download
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(preview.id)}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Delete
                </button>
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Open in new tab
                </a>
              </div>
            </div>
          </div>
        )}

        {error && <div className="text-red-500 mt-4">{error}</div>}
      </div>
    </div>
  );
}
